import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, ChevronLeft, ChevronRight, Database, GitBranch, LineChart, LockKeyhole, ShieldCheck, Zap } from "lucide-react";
import { Badge } from "../lib/ui";
import StorySpine from "../components/StorySpine";
import StrategyChain from "../components/StrategyChain";
import ConnectorActionModal from "../components/ConnectorActionModal";
import SubFlowNav, { FocusPanel, MetricStrip, SubFlowPager } from "../components/SubFlow";
import { getEnterpriseStrategy, getBrdMaster } from "../lib/api";
import { PRODUCT_BOUNDARY, LOGICAL_LAYERS, MASTER_JOURNEY } from "../lib/story";
import { showStorySpine } from "../lib/master";
import { useGuide } from "../lib/guide";
import { useSubFlow } from "../lib/subflow";
import {
  ENTERPRISE_DRILLS,
  ENTERPRISE_FLOWS,
  ENTERPRISE_FOCUS,
  enterpriseFocusHref,
} from "../lib/enterpriseFlows";
import { ENTERPRISE_OUTCOME_CARDS, ENTERPRISE_SOURCE_CARDS, connectorIntakeHref } from "../lib/connectors";

const ICONS = {
  cmdb: Database,
  github: GitBranch,
  jira: LineChart,
  servicenow: ShieldCheck,
  observability: Zap,
  finops: Building2,
  confluence: Database,
};

const RIGHT_ICONS = {
  "cloud-platforms": Building2,
  cicd: GitBranch,
  sre: Zap,
  grc: ShieldCheck,
  "erp-finance": LineChart,
};

const INVARIANTS = [
  "Does not execute modernization in Jira, CI/CD or cloud",
  "Does not replace CMDB, ServiceNow, Confluence or FinOps",
  "Does not become Jira + ServiceNow + CAST + CI/CD + chatbot",
  "AI cannot authorize an MDR",
  "AI cannot pass G7 / G8",
  "Unknown never becomes false",
  "Authorized MDRs are immutable",
  "Deployment ≠ Production Success",
  "No cross-tenant data leakage",
];

export default function Enterprise({ app }) {
  const guide = useGuide();
  const navigate = useNavigate();
  const { focus } = useSubFlow("overview");
  const [policy, setPolicy] = useState(null);
  const [master, setMaster] = useState(null);
  const [connectorModal, setConnectorModal] = useState(null);
  const appId = app?.id;
  const isOverview = focus === "overview";
  const meta = ENTERPRISE_FOCUS[focus] || ENTERPRISE_FOCUS.overview;

  useEffect(() => {
    getEnterpriseStrategy()
      .then((r) => setPolicy(r.data))
      .catch(() => setPolicy(null));
    getBrdMaster()
      .then((r) => setMaster(r.data))
      .catch(() => setMaster(null));
  }, []);

  const strategy = app?.enterprise_strategy || (policy ? {
    catalog: policy,
    chain: [
      { id: "strategy", label: "Strategy", detail: "Tenant technology principles (Hosting → Delivery)." },
      { id: "guardrails", label: "Guardrails", detail: "ADOPTED must be met. EXCEPTION blocks a path. UNKNOWN is not a yes." },
      { id: "assessment", label: "Assessment", detail: "Select an application to score Replatform vs Refactor." },
      { id: "recommendation", label: "Recommendation", detail: "Stronger strategic alignment is recommended. It is not an authorized MDR." },
    ],
    contest: { applicable: false, reason: "Select an application to run the Replatform vs Refactor contest." },
    guardrails: [],
    recommendation: { why: ["Select an application."] },
  } : null);

  return (
    <div className="inner-page subflow-page enterprise-page" data-testid="enterprise-page">
      {!isOverview && (
        <Link to={enterpriseFocusHref(appId, "overview")} className="analytics-back" data-testid="enterprise-back">
          <ChevronLeft size={16} /> Back to overview
        </Link>
      )}

      <FocusPanel
        kicker={`ENTERPRISE FIT${appId ? ` · ${appId}` : ""}`}
        title={meta.title}
        detail={isOverview ? `${PRODUCT_BOUNDARY} Journey: ${MASTER_JOURNEY}.` : meta.detail}
        badge={<Badge tone="cyan">{master?.module_count || 20} modules</Badge>}
        testId="enterprise-head"
      />

      {!isOverview && (
        <>
          <SubFlowNav items={ENTERPRISE_FLOWS} focus={focus} appId={appId} testId="enterprise-subflow" />
          <SubFlowPager items={ENTERPRISE_FLOWS} focus={focus} appId={appId} testId="enterprise-pager" />
          {showStorySpine(guide) && <StorySpine current={focus === "connectors" ? "monitor" : "design"} compact />}
        </>
      )}

      {isOverview && (
        <>
          <MetricStrip
            items={[
              { label: "Logical layers", value: LOGICAL_LAYERS.length, detail: "Destination architecture intent", testId: "enterprise-kpi-layers" },
              { label: "Source connectors", value: ENTERPRISE_SOURCE_CARDS.length, detail: "Authoritative facts in", testId: "enterprise-kpi-sources" },
              { label: "Outcome systems", value: ENTERPRISE_OUTCOME_CARDS.length, detail: "Value evidence out", testId: "enterprise-kpi-outcomes" },
              { label: "BRD modules", value: master?.module_count || 20, detail: master?.journey || MASTER_JOURNEY, testId: "enterprise-kpi-modules" },
            ]}
          />

          <section className="feature-panel analytics-panel" data-testid="enterprise-drill-links">
            <div className="panel-header compact">
              <h2>Drill down — one topic at a time</h2>
            </div>
            <div className="lifecycle-phase-links">
              {ENTERPRISE_DRILLS.map(({ id, label }) => (
                <Link key={id} to={enterpriseFocusHref(appId, id)} className="lifecycle-phase-link" data-testid={`enterprise-drill-${id}`}>
                  <strong>{label}</strong>
                  <span>{ENTERPRISE_FOCUS[id]?.detail}</span>
                  <ChevronRight size={16} />
                </Link>
              ))}
            </div>
          </section>

          <p className="muted small-note" data-testid="enterprise-boundary">
            Five logical layers are intent, not separately deployed services. G1–G8 are unchanged.
          </p>
        </>
      )}

      {focus === "strategy" && (
        <>
          <StrategyChain strategy={strategy} showTaxonomy />

          <div className="feature-panel" data-testid="enterprise-invariants">
            <div className="panel-header">
              <div>
                <p className="kicker">GOVERNANCE INVARIANTS</p>
                <h2>What YUG360-MIND will never do</h2>
              </div>
              <Badge tone="rose">Non-negotiable</Badge>
            </div>
            <div className="invariant-grid">
              {INVARIANTS.map((item) => (
                <div key={item} className="invariant"><LockKeyhole size={13} /> {item}</div>
              ))}
            </div>
          </div>
        </>
      )}

      {focus === "connectors" && (
        <div className="enterprise-map" data-testid="enterprise-map">
          <div className="ent-column" id="spine-monitor">
            <p className="kicker">EXISTING ENTERPRISE TOOLS</p>
            {ENTERPRISE_SOURCE_CARDS.map(({ id, name, detail }) => {
              const Icon = ICONS[id] || Database;
              return (
                <div key={id} className="ent-card" data-testid={`ent-left-${id}`}>
                  <span className="ent-icon"><Icon size={16} /></span>
                  <div><strong>{name}</strong><small>{detail}</small></div>
                  <button
                    type="button"
                    className="connector-action-badge cyan"
                    data-testid={`connector-read-${id}`}
                    onClick={() => (appId ? setConnectorModal(id) : navigate("/applications"))}
                  >
                    Read authoritative facts
                  </button>
                </div>
              );
            })}
          </div>

          <div className="ent-core" id="spine-design">
            <div className="ent-core-inner">
              <p className="kicker">YUG360-MIND · ENTERPRISE MODERNIZATION INTELLIGENCE SYSTEM</p>
              <ul data-testid="enterprise-layers">
                {LOGICAL_LAYERS.map((layer) => (
                  <li key={layer}><LockKeyhole size={14} /> {layer}</li>
                ))}
                <li><LockKeyhole size={14} /> Canonical evidence &amp; provenance</li>
                <li><LockKeyhole size={14} /> 7R taxonomy of competing hypotheses</li>
                <li><LockKeyhole size={14} /> Immutable MDR (decision memory, lean)</li>
                <li><LockKeyhole size={14} /> G1–G8 · WHY → expected → actual</li>
              </ul>
              <small>Layers above are intent. Model=None supported · AI is advisory · Tenant is a hard security boundary. Zero-copy / model gateway / live connectors are Next — import replay (CSV/JSON) is Now.</small>
            </div>
          </div>

          <div className="ent-column">
            <p className="kicker">DELIVERY / PRODUCTION / VALUE SYSTEMS</p>
            {ENTERPRISE_OUTCOME_CARDS.map(({ id, name, detail }) => {
              const Icon = RIGHT_ICONS[id] || LineChart;
              return (
                <div key={id} className="ent-card" data-testid={`ent-right-${id}`}>
                  <span className="ent-icon"><Icon size={16} /></span>
                  <div><strong>{name}</strong><small>{detail}</small></div>
                  <button
                    type="button"
                    className="connector-action-badge green"
                    data-testid={`connector-feed-${id}`}
                    onClick={() => (appId ? setConnectorModal(id) : navigate("/applications"))}
                  >
                    Feeds outcome evidence
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {connectorModal && (
        <ConnectorActionModal
          connectorId={connectorModal}
          appId={appId}
          onClose={() => setConnectorModal(null)}
          onImportReplay={() => {
            setConnectorModal(null);
            navigate(connectorIntakeHref(appId, connectorModal));
          }}
        />
      )}

      {focus === "modules" && master && (
        <section className="feature-panel" data-testid="master-module-map">
          <div className="panel-header">
            <div>
              <p className="kicker">MASTER BRD v{master.version} · 20 MODULES · DESTINATION MAP</p>
              <h2>{master.journey}</h2>
              <p className="muted">Now / Lean / Next overlay. This workspace does not rename G1–G8 and does not replace Jira, CMDB or CI/CD.</p>
            </div>
            <Badge tone="cyan">{master.module_count} modules</Badge>
          </div>
          <div className="module-map">
            {(master.modules || []).map((mod) => (
              <div key={mod.n} className={`module-chip altitude-${mod.altitude}`} data-testid={`master-module-${mod.n}`}>
                <small>{String(mod.n).padStart(2, "0")} · {mod.pillar.toUpperCase()} · {mod.altitude}</small>
                <strong>{mod.name}</strong>
                <span>{mod.note}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {focus === "modules" && !master && (
        <p className="muted small-note" data-testid="enterprise-modules-loading">Loading master BRD module map…</p>
      )}

      {!isOverview && (
        <SubFlowPager items={ENTERPRISE_FLOWS} focus={focus} appId={appId} testId="enterprise-pager-bottom" />
      )}
    </div>
  );
}
